import { FocusPluginRef } from '@/components/editor/FocusPlugin';
import { $createPlaceholderTicketNode } from '@/components/editor/TicketPlugin/PlaceholderTicketNode';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $createListItemNode,
  $createListNode,
  $isListItemNode,
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {
  AtSign,
  Bold,
  Code,
  FileCode,
  Hash,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link,
  List,
  ListOrdered,
  ListTodo,
  Palette,
  Quote,
  Strikethrough,
  Type,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

const blockTypeToBlockName = {
  paragraph: 'Normal',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  quote: 'Quote',
  code: 'Code Block',
  bullet: 'Bulleted List',
  number: 'Numbered List',
  check: 'Task List',
};

type BlockType = keyof typeof blockTypeToBlockName;

const getBlockTypeIcon = (blockType: BlockType) => {
  switch (blockType) {
    case 'h1':
      return <Heading1 className="h-4 w-4" />;
    case 'h2':
      return <Heading2 className="h-4 w-4" />;
    case 'h3':
      return <Heading3 className="h-4 w-4" />;
    case 'quote':
      return <Quote className="h-4 w-4" />;
    case 'code':
      return <FileCode className="h-4 w-4" />;
    case 'bullet':
      return <List className="h-4 w-4" />;
    case 'number':
      return <ListOrdered className="h-4 w-4" />;
    case 'check':
      return <ListTodo className="h-4 w-4" />;
    case 'paragraph':
    default:
      return <Type className="h-4 w-4" />;
  }
};

const getStyleIcon = (toolbarState: ToolbarState) => {
  const activeStyles = [
    toolbarState.isBold,
    toolbarState.isItalic,
    toolbarState.isStrikethrough,
    toolbarState.isCode,
  ].filter(Boolean);

  // If exactly one style is active, show its icon
  if (activeStyles.length === 1) {
    if (toolbarState.isBold) return <Bold className="h-4 w-4" />;
    if (toolbarState.isItalic) return <Italic className="h-4 w-4" />;
    if (toolbarState.isStrikethrough)
      return <Strikethrough className="h-4 w-4" />;
    if (toolbarState.isCode) return <Code className="h-4 w-4" />;
  }

  // Otherwise, show the generic palette icon
  return <Palette className="h-4 w-4" />;
};

interface ToolbarState {
  isBold: boolean;
  isItalic: boolean;
  isStrikethrough: boolean;
  isCode: boolean;
  blockType: BlockType;
  isLink: boolean;
  linkUrl: string;
}

interface ToolbarPluginProps {
  isMarkdownMode?: boolean;
  onToggleMarkdownMode?: () => void;
  focusPluginRef?: React.RefObject<FocusPluginRef | null>;
  markdownTextareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

export function ToolbarPlugin({
  isMarkdownMode = false,
  onToggleMarkdownMode,
  focusPluginRef,
  markdownTextareaRef,
}: ToolbarPluginProps = {}): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [toolbarState, setToolbarState] = useState<ToolbarState>({
    isBold: false,
    isItalic: false,
    isStrikethrough: false,
    isCode: false,
    blockType: 'paragraph',
    isLink: false,
    linkUrl: '',
  });
  const [textTypeDropdownOpen, setTextTypeDropdownOpen] = useState(false);
  const [styleDropdownOpen, setStyleDropdownOpen] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkInputValue, setLinkInputValue] = useState('');

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Collect all state in one object to avoid multiple setToolbarState calls
        const newState: Partial<ToolbarState> = {};

        // Update text formatting
        newState.isBold = selection.hasFormat('bold');
        newState.isItalic = selection.hasFormat('italic');
        newState.isStrikethrough = selection.hasFormat('strikethrough');
        newState.isCode = selection.hasFormat('code');

        // Update link state
        const anchorNode = selection.anchor.getNode();
        const focusNode = selection.focus.getNode();
        const linkParent = $findMatchingParent(anchorNode, $isLinkNode);
        const focusLinkParent = $findMatchingParent(focusNode, $isLinkNode);

        if (linkParent && linkParent === focusLinkParent) {
          newState.isLink = true;
          newState.linkUrl = linkParent.getURL();
        } else {
          newState.isLink = false;
          newState.linkUrl = '';
        }

        // Update block type
        const element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow();
        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);

        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = element;
            const type = parentList.getListType();
            newState.blockType =
              type === 'number'
                ? 'number'
                : type === 'check'
                  ? 'check'
                  : 'bullet';
          } else {
            const type = $isHeadingNode(element)
              ? element.getTag()
              : element.getType();
            if (type in blockTypeToBlockName) {
              newState.blockType = type as BlockType;
            }
          }
        }

        // Update state once with all changes
        setToolbarState((prev) => ({
          ...prev,
          ...newState,
        }));
      }
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        1,
      ),
    );
  }, [editor, updateToolbar]);

  const formatText = useCallback(
    (format: 'bold' | 'italic' | 'strikethrough' | 'code') => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [editor],
  );

  const formatParagraph = useCallback(() => {
    if (toolbarState.blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [editor, toolbarState.blockType]);

  const formatHeading = useCallback(
    (headingSize: 'h1' | 'h2' | 'h3') => {
      if (toolbarState.blockType !== headingSize) {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode(headingSize));
          }
        });
      }
    },
    [editor, toolbarState.blockType],
  );

  const formatQuote = useCallback(() => {
    if (toolbarState.blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  }, [editor, toolbarState.blockType]);

  const formatBulletList = useCallback(() => {
    if (toolbarState.blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  }, [editor, formatParagraph, toolbarState.blockType]);

  const formatNumberedList = useCallback(() => {
    if (toolbarState.blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  }, [editor, formatParagraph, toolbarState.blockType]);

  const formatCheckList = useCallback(() => {
    if (toolbarState.blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  }, [editor, formatParagraph, toolbarState.blockType]);

  useEffect(() => {
    if (toolbarState.isLink) {
      setLinkInputValue(toolbarState.linkUrl);
    } else {
      setLinkInputValue('');
    }
  }, [toolbarState.isLink, toolbarState.linkUrl]);

  const handleLinkSubmit = useCallback(() => {
    if (linkInputValue.trim() === '') {
      // Clear link
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      // Set or update link
      const url = linkInputValue.startsWith('http')
        ? linkInputValue
        : `https://${linkInputValue}`;
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }
    setLinkPopoverOpen(false);
    setLinkInputValue('');
  }, [editor, linkInputValue]);

  const handleTextTypeDropdownOpenChange = useCallback((open: boolean) => {
    setTextTypeDropdownOpen(open);
  }, []);

  const handleStyleDropdownOpenChange = useCallback((open: boolean) => {
    setStyleDropdownOpen(open);
  }, []);

  const handleLinkPopoverOpenChange = useCallback((open: boolean) => {
    setLinkPopoverOpen(open);
  }, []);

  // Handle quick access letter navigation in dropdowns
  const handleTextTypeKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key.toLowerCase();
      event.stopPropagation();

      switch (key) {
        case 'p':
          event.preventDefault();
          formatParagraph();
          handleTextTypeDropdownOpenChange(false);
          break;
        case '1':
          event.preventDefault();
          formatHeading('h1');
          handleTextTypeDropdownOpenChange(false);
          break;
        case '2':
          event.preventDefault();
          formatHeading('h2');
          handleTextTypeDropdownOpenChange(false);
          break;
        case '3':
          event.preventDefault();
          formatHeading('h3');
          handleTextTypeDropdownOpenChange(false);
          break;
        case 'q':
          event.preventDefault();
          formatQuote();
          handleTextTypeDropdownOpenChange(false);
          break;
        case 'b':
          event.preventDefault();
          formatBulletList();
          handleTextTypeDropdownOpenChange(false);
          break;
        case 'n':
          event.preventDefault();
          formatNumberedList();
          handleTextTypeDropdownOpenChange(false);
          break;
        case 't':
          event.preventDefault();
          formatCheckList();
          handleTextTypeDropdownOpenChange(false);
          break;
      }
    },
    [
      formatParagraph,
      formatHeading,
      formatQuote,
      formatBulletList,
      formatNumberedList,
      formatCheckList,
      handleTextTypeDropdownOpenChange,
    ],
  );

  const handleStyleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key.toLowerCase();
      event.stopPropagation();

      switch (key) {
        case 'b':
          event.preventDefault();
          formatText('bold');
          handleStyleDropdownOpenChange(false);
          break;
        case 'i':
          event.preventDefault();
          formatText('italic');
          handleStyleDropdownOpenChange(false);
          break;
        case 's':
          event.preventDefault();
          formatText('strikethrough');
          handleStyleDropdownOpenChange(false);
          break;
        case 'c':
          event.preventDefault();
          formatText('code');
          handleStyleDropdownOpenChange(false);
          break;
      }
    },
    [formatText, handleStyleDropdownOpenChange],
  );

  const insertAtSymbol = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Check if we need a space before @
        const anchorNode = selection.anchor.getNode();
        const offset = selection.anchor.offset;

        // Get the text before cursor
        let needsSpace = false;
        if (anchorNode.getTextContent && offset > 0) {
          const textBefore = anchorNode.getTextContent().charAt(offset - 1);
          needsSpace = textBefore !== ' ' && textBefore !== '\n';
        }

        const textToInsert = needsSpace ? ' @' : '@';
        selection.insertText(textToInsert);
      }
    });
  }, [editor]);

  const insertHashSymbol = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      // Check if selection is empty (cursor position only)
      if (selection.isCollapsed()) {
        // EXISTING BEHAVIOR: Insert # at cursor
        const anchorNode = selection.anchor.getNode();
        const offset = selection.anchor.offset;

        // Get the text before cursor
        let needsSpace = false;
        if (anchorNode.getTextContent && offset > 0) {
          const textBefore = anchorNode.getTextContent().charAt(offset - 1);
          needsSpace = textBefore !== ' ' && textBefore !== '\n';
        }

        const textToInsert = needsSpace ? ' #' : '#';
        selection.insertText(textToInsert);
      } else {
        // NEW BEHAVIOR: Convert selection to placeholder(s)

        // Helper: Extract lines from nodes
        const extractLinesFromNodes = (nodes: LexicalNode[]): string[] => {
          const lines: string[] = [];
          const seenText = new Set<string>();
          nodes.forEach((node) => {
            const text = node.getTextContent().trim();
            if (text && !seenText.has(text)) {
              seenText.add(text);
              lines.push(text);
            }
          });
          return lines;
        };

        // Helper: Detect if selection spans multiple lines
        const detectMultiLine = (): boolean => {
          const anchorNode = selection.anchor.getNode();
          const focusNode = selection.focus.getNode();
          const anchorElement = anchorNode.getTopLevelElementOrThrow();
          const focusElement = focusNode.getTopLevelElementOrThrow();
          if (anchorElement !== focusElement) return true;
          const text = selection.getTextContent();
          if (text.includes('\n')) return true;
          const nodes = selection.getNodes();
          const topLevelElements = new Set();
          nodes.forEach((node) => {
            topLevelElements.add(node.getTopLevelElementOrThrow());
          });
          return topLevelElements.size > 1;
        };

        // Helper: Get all list item nodes from selection (expands to full items)
        const getListItemsFromSelection = (): LexicalNode[] => {
          const nodes = selection.getNodes();
          const listItems = new Set<LexicalNode>();
          nodes.forEach((node) => {
            const listItem = $findMatchingParent(node, $isListItemNode);
            if (listItem) {
              listItems.add(listItem);
            } else {
              listItems.add(node);
            }
          });
          return Array.from(listItems);
        };

        // Detect if multi-line
        const isMultiLine = detectMultiLine();

        if (isMultiLine) {
          // Multi-line: Create list with placeholders
          const nodes = getListItemsFromSelection();
          const lines = extractLinesFromNodes(nodes);
          if (lines.length === 0) return;

          const anchorNode = selection.anchor.getNode();
          const listParent = $findMatchingParent(anchorNode, $isListNode);
          const listType = listParent ? listParent.getListType() : 'bullet';

          // Create new list with placeholders
          const newList = $createListNode(listType);
          lines.forEach((lineText) => {
            const listItemNode = $createListItemNode();
            const placeholderNode = $createPlaceholderTicketNode(lineText);
            listItemNode.append(placeholderNode);
            newList.append(listItemNode);
          });

          // Remove the old nodes (expanded list items) before inserting new ones
          nodes.forEach((node) => {
            node.remove();
          });

          // Insert the new list at the selection position
          selection.insertNodes([newList]);
        } else {
          // Single-line: Create one placeholder
          const selectedText = selection.getTextContent().trim();
          if (!selectedText) return; // Whitespace-only

          const placeholderNode = $createPlaceholderTicketNode(selectedText);
          selection.insertNodes([placeholderNode]);
        }
      }
    });
  }, [editor]);

  // Keyboard shortcuts handler for Lexical editor
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { key, ctrlKey, metaKey, shiftKey } = event;
      const isCtrl = ctrlKey || metaKey; // Support both Ctrl and Cmd

      // Ctrl+Shift+M: Toggle markdown mode
      if (isCtrl && shiftKey && key.toLowerCase() === 'm') {
        event.preventDefault();
        event.stopPropagation();
        onToggleMarkdownMode?.();
        return;
      }

      // Ctrl+B/I: Bold/Italic - prevent browser actions
      if (isCtrl && !shiftKey) {
        if (key.toLowerCase() === 'b') {
          event.preventDefault();
          event.stopPropagation();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          return;
        }
        if (key.toLowerCase() === 'i') {
          event.preventDefault();
          event.stopPropagation();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          return;
        }
        if (key.toLowerCase() === 's') {
          event.preventDefault();
          event.stopPropagation();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          return;
        }
      }

      // Ctrl+P: Open text/node type dropdown
      if (isCtrl && !shiftKey && key.toLowerCase() === 'p') {
        event.preventDefault();
        event.stopPropagation();
        handleTextTypeDropdownOpenChange(true);
        return;
      }

      // Ctrl+F: Open font/styling dropdown
      if (isCtrl && !shiftKey && key.toLowerCase() === 'f') {
        event.preventDefault();
        event.stopPropagation();
        handleStyleDropdownOpenChange(true);
        return;
      }

      // Ctrl+L: Open link popover
      if (isCtrl && !shiftKey && key.toLowerCase() === 'l') {
        event.preventDefault();
        event.stopPropagation();
        setLinkPopoverOpen(true);
        return;
      }
    };

    return editor.registerRootListener(
      (
        rootElement: null | HTMLElement,
        prevRootElement: null | HTMLElement,
      ) => {
        if (prevRootElement !== null) {
          prevRootElement.removeEventListener('keydown', handleKeyDown, {
            capture: true,
          });
        }
        if (rootElement !== null) {
          rootElement.addEventListener('keydown', handleKeyDown, {
            capture: true,
          });
        }
      },
    );
  }, [
    editor,
    onToggleMarkdownMode,
    handleTextTypeDropdownOpenChange,
    handleStyleDropdownOpenChange,
    setLinkPopoverOpen,
  ]);

  // Keyboard shortcuts handler for markdown textarea
  useEffect(() => {
    const textareaElement = markdownTextareaRef?.current;
    if (!textareaElement) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const { key, ctrlKey, metaKey, shiftKey } = event;
      const isCtrl = ctrlKey || metaKey;

      // Ctrl+Shift+M: Toggle markdown mode
      if (isCtrl && shiftKey && key.toLowerCase() === 'm') {
        event.preventDefault();
        onToggleMarkdownMode?.();
      }
    };

    textareaElement.addEventListener('keydown', handleKeyDown);

    return () => {
      textareaElement.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdownTextareaRef?.current, onToggleMarkdownMode]);

  return (
    <div className="text-muted-foreground flex items-center gap-2 rounded-t-md px-1 pt-1">
      {/* Node Type Selector */}
      <DropdownMenu
        open={textTypeDropdownOpen}
        onOpenChange={handleTextTypeDropdownOpenChange}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            tabIndex={-1}
            title="Text type (Ctrl+P)"
            disabled={isMarkdownMode}
          >
            {getBlockTypeIcon(toolbarState.blockType)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onKeyDown={handleTextTypeKeyDown}
          onCloseAutoFocus={focusPluginRef?.current?.focus}
        >
          <DropdownMenuItem onClick={formatParagraph}>
            <Type className="mr-2 h-4 w-4" />
            Paragraph
            <DropdownMenuShortcut>P</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h1')}>
            <Heading1 className="mr-2 h-4 w-4" />
            Heading 1 <DropdownMenuShortcut>1</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h2')}>
            <Heading2 className="mr-2 h-4 w-4" />
            Heading 2 <DropdownMenuShortcut>2</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h3')}>
            <Heading3 className="mr-2 h-4 w-4" />
            Heading 3 <DropdownMenuShortcut>3</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={formatQuote}>
            <Quote className="mr-2 h-4 w-4" />
            Quote
            <DropdownMenuShortcut>Q</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={formatBulletList}>
            <List className="mr-2 h-4 w-4" />
            Bullet List
            <DropdownMenuShortcut>B</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={formatNumberedList}>
            <ListOrdered className="mr-2 h-4 w-4" />
            Numbered List
            <DropdownMenuShortcut>N</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={formatCheckList}>
            <ListTodo className="mr-2 h-4 w-4" />
            Task List
            <DropdownMenuShortcut>T</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Styling Dropdown */}
      <DropdownMenu
        open={styleDropdownOpen}
        onOpenChange={handleStyleDropdownOpenChange}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            tabIndex={-1}
            title="Text style (Ctrl+F)"
            disabled={isMarkdownMode}
          >
            {getStyleIcon(toolbarState)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onKeyDown={handleStyleKeyDown}
          onCloseAutoFocus={focusPluginRef?.current?.focus}
        >
          <DropdownMenuItem
            onClick={() => formatText('bold')}
            className={
              toolbarState.isBold ? 'bg-accent text-accent-foreground' : ''
            }
          >
            <Bold className="mr-2 h-4 w-4" />
            Bold
            <DropdownMenuShortcut>B</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => formatText('italic')}
            className={
              toolbarState.isItalic ? 'bg-accent text-accent-foreground' : ''
            }
          >
            <Italic className="mr-2 h-4 w-4" />
            Italic
            <DropdownMenuShortcut>I</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => formatText('strikethrough')}
            className={
              toolbarState.isStrikethrough
                ? 'bg-accent text-accent-foreground'
                : ''
            }
          >
            <Strikethrough className="mr-2 h-4 w-4" />
            Strikethrough
            <DropdownMenuShortcut>S</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => formatText('code')}
            className={
              toolbarState.isCode ? 'bg-accent text-accent-foreground' : ''
            }
          >
            <Code className="mr-2 h-4 w-4" />
            Code
            <DropdownMenuShortcut>C</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Individual Action Buttons */}
      <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={toolbarState.isLink ? 'default' : 'ghost'}
            size="sm"
            type="button"
            onClick={() => setLinkPopoverOpen(true)}
            tabIndex={-1}
            title="Insert/edit link (Ctrl+L)"
            disabled={isMarkdownMode}
          >
            <Link className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80"
          onCloseAutoFocus={focusPluginRef?.current?.focus}
        >
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="leading-none font-medium">Link URL</h4>
              <p className="text-muted-foreground text-sm">
                {toolbarState.isLink
                  ? 'Update or clear the link'
                  : 'Add a link to the selected text'}
              </p>
            </div>
            <div className="grid gap-2">
              <Input
                placeholder="https://example.com"
                value={linkInputValue}
                onChange={(e) => setLinkInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLinkSubmit();
                  } else if (e.key === 'Escape') {
                    setLinkPopoverOpen(false);
                    setLinkInputValue('');
                  }
                }}
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" type="button" onClick={handleLinkSubmit}>
                  {toolbarState.isLink ? 'Update' : 'Add'}
                </Button>
                {toolbarState.isLink && (
                  <Button
                    size="sm"
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                      handleLinkPopoverOpenChange(false);
                      setLinkInputValue('');
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={insertAtSymbol}
        title="Insert mention (@)"
        tabIndex={-1}
        disabled={isMarkdownMode}
      >
        <AtSign className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={insertHashSymbol}
        title="Insert ticket reference (#)"
        tabIndex={-1}
        disabled={isMarkdownMode}
      >
        <Hash className="h-4 w-4" />
      </Button>

      {/* Markdown Mode Toggle */}
      {onToggleMarkdownMode && (
        <Button
          variant={isMarkdownMode ? 'default' : 'ghost'}
          size="sm"
          type="button"
          onClick={onToggleMarkdownMode}
          title="Toggle markdown mode (Ctrl+Shift+M)"
          tabIndex={-1}
        >
          <FileCode className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
